"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import Link from "next/link";
import { db } from "@/lib/firebase";
import { collection, getDocs, query, where, limit } from "firebase/firestore";
import type { Item } from "@/types/marketplace";
import type { Event } from "@/types/event";

interface AdItem {
  id: string;
  title: string;
  imageUrl: string;
  href: string;
  type: "marketplace" | "event";
}

type Phase = "idle" | "entering" | "expanding" | "display" | "leaving";

const BASE_W = 50; // matches --header-height (50px)

function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

interface Props {
  enabled: boolean;
  cycleInterval: number;
  displayDuration: number;
  expandRatio: number;
  sources: string[];
}

export function HeaderAdBanner({
  enabled,
  cycleInterval,
  displayDuration,
  expandRatio,
  sources,
}: Props) {
  const [ads, setAds] = useState<AdItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Fetch ad items on mount
  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    async function fetchAds() {
      const results: AdItem[] = [];

      if (sources.includes("marketplace")) {
        try {
          const q = query(
            collection(db, "item"),
            where("status", "==", "active"),
            limit(20)
          );
          const snap = await getDocs(q);
          snap.docs.forEach((doc) => {
            const item = { itemId: doc.id, ...doc.data() } as Item;
            if (item.adminHidden) return;
            const img = item.media?.find((m) => m.type === "image");
            if (!img?.url) return;
            results.push({
              id: item.itemId,
              title: item.title,
              imageUrl: img.url,
              href: `/marketplace/${item.itemId}`,
              type: "marketplace",
            });
          });
        } catch {
          // silently ignore — banner is non-critical
        }
      }

      if (sources.includes("events")) {
        try {
          const q = query(
            collection(db, "events"),
            where("eventStatus", "==", "upcoming"),
            limit(20)
          );
          const snap = await getDocs(q);
          snap.docs.forEach((doc) => {
            const ev = { id: doc.id, ...doc.data() } as Event;
            if (!ev.imageUrl) return;
            results.push({
              id: ev.id,
              title: ev.title,
              imageUrl: ev.imageUrl,
              href: `/events/${ev.id}`,
              type: "event",
            });
          });
        } catch {
          // silently ignore
        }
      }

      if (!cancelled) {
        setAds(shuffle(results));
      }
    }

    fetchAds();
    return () => {
      cancelled = true;
    };
  }, [enabled, sources]);

  // Clear any pending timer on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const scheduleNext = useCallback(
    (delayMs: number, fn: () => void) => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        if (mountedRef.current) fn();
      }, delayMs);
    },
    []
  );

  // State machine driver
  useEffect(() => {
    if (!enabled || ads.length === 0) return;

    if (phase === "idle") {
      // Wait cycleInterval seconds then enter with the next ad
      scheduleNext(cycleInterval * 1000, () => setPhase("entering"));
    } else if (phase === "entering") {
      // After 400ms (transition settling) expand
      scheduleNext(400, () => setPhase("expanding"));
    } else if (phase === "expanding") {
      // After 600ms (expansion transition) hold
      scheduleNext(600, () => setPhase("display"));
    } else if (phase === "display") {
      // Hold for displayDuration seconds then leave
      scheduleNext(displayDuration * 1000, () => setPhase("leaving"));
    } else if (phase === "leaving") {
      // After leave transition ends, advance index and go idle
      scheduleNext(350, () => {
        setCurrentIndex((i) => (i + 1) % ads.length);
        setPhase("idle");
      });
    }
  }, [phase, ads.length, enabled, cycleInterval, displayDuration, scheduleNext]);

  // Don't render if disabled or no ads
  if (!enabled || ads.length === 0) return null;

  const currentAd = ads[currentIndex];

  const currentWidth =
    phase === "idle" || phase === "leaving"
      ? 0
      : phase === "entering"
      ? BASE_W
      : BASE_W * expandRatio; // expanding + display

  const opacity = phase === "idle" || phase === "leaving" ? 0 : 1;

  const transitionDuration =
    phase === "expanding" ? "500ms" : "300ms";

  return (
    <div
      className="relative flex-shrink-0 overflow-hidden transition-all ease-in-out h-full"
      style={{
        width: currentWidth,
        opacity,
        transitionDuration,
        transitionProperty: "width, opacity",
      }}
    >
      {currentAd && (
        <Link href={currentAd.href} className="block h-full w-full">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={currentAd.imageUrl}
            alt={currentAd.title}
            className="h-full w-full object-cover"
          />
          <span className="absolute bottom-0 left-0 right-0 text-[8px] text-center bg-black/50 text-white truncate px-1 leading-4">
            {currentAd.title}
          </span>
        </Link>
      )}
    </div>
  );
}
